import { useStore } from '@nanostores/react';
import { accountStore, authStore } from './store';
import type { AugmentedAccount } from './types';

export const AccountFeatureFlag = {
  Whatsapp: 'whatsapp',
  StoreAnonEvents: 'store_anon_events',
  JavascriptWebhook: 'javascript_webhook',
  LegacyCustomSmtpBilling: 'legacy_custom_smtp_billing',
  SubscriptionCenterM1: 'subscription_center_m1',
  MultiLanguageSettings: 'multi_language_settings',
  ExitOnlyAfterMessage: 'exit_only_after_message',
  ParcelAmp: 'parcel_amp',
  CdpDataCatalogEnabled: 'cdp_data_catalog_enabled',
  VirtualSupportChatEnabled: 'virtual_support_chat_enabled',
  ParcelInHydra: 'parcel_in_hydra',
  InAppLegacyEditor: 'in_app_legacy_editor',
  TourtipAdventuresV1: 'tourtip_adventures_v1',
  FlagCDPUIConsolidation: 'cdp_ui_consolidation',

  GoalsMilestones: 'goals-milestones',
  GoalsOnboardingRecommendations: 'goals-onboarding-recommendations',
  GoalsEmbed: 'goals-embed',
  OutcomeOptimization: 'outcome_optimization',
  McpExternalAccountToggle: 'mcp-external-account-toggle',
  TasksPerformanceDashboard: 'tasks_performance_dashboard_enabled',
  SegmentAutoArchive: 'segment_auto_archive',
  JourneysTaxonomyRename: 'journeys_taxonomy_rename',
  JourneysTaxonomyRenameFinal: 'journeys_taxonomy_rename_final',
  // Pulled from Ember feature flags
  ActivityLogsTimeRange: 'activity-logs-time-range',
  DryadGoalsBuilder: 'dryad-goals-builder',
  DryadCreateSegmentBuilder: 'dryad-create-segment-builder',
  DryadObjectsBuilder: 'dryad-objects-builder',
  DryadPeopleSearchBuilder: 'dryad-people-search-builder',
  DryadPeopleExportBuilder: 'dryad-people-export-builder',
  DryadPeopleBulkDeleteBuilder: 'dryad-people-bulk-delete-builder',
  DryadNewsLettersBuilder: 'dryad-newsletters-builder',
  DryadFormsEventLogBuilder: 'dryad-forms-event-log-builder',
  DryadActionComposerBuilder: 'dryad-action-composer-builder',
  DryadBatchUpdateBuilder: 'dryad-batch-update-builder',
  DryadPreconditionsBuilder: 'dryad-action-preconditions-builder',
  DryadRecipientsBuilder: 'dryad-recipients-builder',
  DryadInAppBroadcastGoalBuilder: 'dryad-in-app-broadcast-goal-builder',
  DryadCampaignTriggersBuilder: 'dryad-campaign-triggers-builder',
  DryadRelSegAttrTriggerBuilder: 'dryad-rel-seg-attr-trigger-builder',
  DryadAttributeTriggerBuilder: 'dryad-attribute-trigger-builder',
  DryadCampaignFiltersBuilder: 'dryad-campaign-filters-builder',
  DryadCampaignAudienceBuilder: 'dryad-campaign-audience-builder',
  DryadConditionalSplitBuilder: 'dryad-conditional-split-builder',
  DryadMultiSplitBuilder: 'dryad-multi-split-builder',
  DryadWaitUntilBuilder: 'dryad-wait-until-builder',
  DryadEventTriggerBuilder: 'dryad-event-trigger-builder',
  DryadConversionGoalBuilder: 'dryad-conversion-goal-builder',
  DryadKnowledgeSourcesBuilder: 'dryad-knowledge-sources-builder',
  DryadExitConditionsBuilder: 'dryad-exit-conditions-builder',
  DryadSegmentsBuilder: 'dryad-segments-builder',
  GoalsCreatePreview: 'goals-create-preview',
  AiAutoSegmentNames: 'ai_auto_segment_names',
  DeliverabilityAnalytics: 'deliverability-analytics',
  TransactionalSms: 'transactional-sms',
  SmsByoForNative: 'sms-byo-for-native',
  ContentFirstInBroadcast: 'content-first-in-broadcast',
  NewsletterRateLimitWithTimezone: 'newsletter-rate-limit-timezone',
  InAppEditorLeadForm: 'in-app-editor-lead-form',
  DefaultFromIdentity: 'default-from-identity',
  EmailAutoTls: 'email-auto-tls',
  ConfiguredWebhooks: 'configured_webhooks',
  LLMActionBYOK: 'llm-action-byok',
  InAppForms: 'in-app-forms',
  TeamMembersExport: 'team_member_export',
  ObjectWaitUntil: 'obj_wait_until',
  JourneyAttributes: 'journey-attributes',
  WhatsappNativeFreeText: 'whatsapp-native-free-text',
  ShowTemplatesSnippetsLayouts: 'show_templates_snippets_layouts',
  InAppGlobalStyles: 'in-app_global-styles',
  InAppMultiApps: 'in-app_multi-apps',
  HalloweenStickies2025: 'halloween_stickies_2025',
  OptimizedMessageLibrary: 'optimized-message-library',

  DsSocialIcons: 'ds-social-icons',
  DsCountdown: 'ds-countdown',
  DsVideo: 'ds-video',
  DsUiPlayground: 'ds-ui-playground',
  ImproveDsAgent: 'improve-ds-agent',

  InAppTooltips: 'in-app-tooltips',
  InAppLivePreview: 'in-app_live-preview',
  DsInfiniteCanvas: 'ds-infinite-canvas',
  DsUseTiptap: 'ds-use-tiptap',
  PausePlanM2: 'pause-plan-m2',
  PavedRoad: 'paved_road',
  DarkerDarkTheme: 'darker-dark-theme',

  MultipleShortlinkDomains: 'multiple_shortlink_domains',
  NotificationsUx: 'notifications-ux',
  HomeUx: 'home-ux',
  ServiceAccounts: 'service-accounts',
  TestDeliveryMode: 'test_delivery_mode',
  TemplateEngineSwitch: 'template_engine_switch',
  AccountVerification: 'account-verification',
  PushV2: 'push_v2',
  MultiApp: 'multi_app',
  DsLiquidExpressions: 'ds-liquid-expressions',
  Gravatar: 'gravatar',
  GravatarEnabled: 'gravatar_enabled',
  AllowAgentEditLiveData: 'allow_agent_edit_live_data',
  McpAllowEditLiveData: 'mcp_allow_edit_live_data',
  McpAllowAccessSensitiveData: 'mcp_allow_access_sensitive_data',
  AgentSnowflake: 'agent_snowflake',
  AgentOpusModel: 'agent-opus-model',
  AgentFloatingLauncher: 'agent_floating_launcher',
  CdpSalesforceEcaMigration: 'cdp_salesforce_eca_migration',
  CdpSalesforceEcaMigrationOptout: 'cdp_salesforce_eca_migration_optout',
  CdpTrackEnabled: 'cdp_track_enabled',
  CdpPassthroughEnabled: 'cdp_passthrough_enabled',
  CdpDatabricksSourceEnabled: 'databricks_source_enabled',
  WhiteLabelTwilio: 'white_label_twilio',
  FrequencyCaps: 'frequency_caps',
  DateTriggeredObjectCampaigns: 'date-triggered-object-campaigns',
  CampaignDraftMode: 'campaign-draft-mode',
  EditorBreadcrumbs: 'editor-breadcrumbs',
  DsInsertMenuPreviews: 'ds-insert-menu-previews',
  DsInboxPreviews: 'ds_inbox_previews',
  DsRecommendedProfiles: 'ds-recommended-profiles',
  PhoneIdentifier: 'phone-identifier',
  OpenTrackingConsent: 'open_tracking_consent',
  ProfileGrowth: 'profile_growth',
  NewPipelinesObjectsPricing: 'new_pipelines_objects_pricing',
  NewPipelinesObjectsBilling: 'new_pipelines_objects_billing',
  BuilderOfferEnabled: 'builder_offer_enabled',
  SlackAgentEnabled: 'slack_agent_enabled',
  GenericSamlSso: 'generic_saml_sso',
  OrdwayCouponCodes: 'ordway_coupon_codes',
  LegacyTwilio: 'legacy-twilio',
  PersonalSettingsInHydra: 'personal_settings_in_hydra',
  EssentialsOnboarding: 'essentials_onboarding',
  CdpJourneysSqlImportAvailable: 'cdp_journeys_sql_import_available',
  SqlSyncMigration: 'sql-sync-migration',
  ReverseEtlConsolidation: 'reverse_etl_consolidation',
  ConsentRecords: 'consent_records',
  SegmentCdpIntegrationsMigration: 'segment-cdp-integrations-migration',
  DashboardEditorPoc: 'dashboard_editor_poc',
  ProfilesMigration: 'profiles-migration',
  WorkflowBuilderMigration: 'workflow-builder-migration',
  ObjectsMigration: 'objects-migration',
  CsvImportsMigration: 'csv-imports-migration',
  BillingUI2026: 'billing-ui-2026',
  HydraSubscriptionCenter: 'hydra-subscription-center',
} as const;

export type AccountFeatureFlag = (typeof AccountFeatureFlag)[keyof typeof AccountFeatureFlag];

export type FeatureFlag = AccountFeatureFlag;
export const FeatureFlag = { ...AccountFeatureFlag };

export const FEATURE_FLAG_DEFAULTS: Record<FeatureFlag, boolean> = Object.freeze({
  outcome_optimization: false, // internal prototype: agent-driven goal A/B testing
  whatsapp: false, // whether to enable sending to whatsapp numbers in Twilio SMS action
  store_anon_events: true, // enables the collection of anonymous events.
  javascript_webhook: false, // enables adding javascript reporting webhook
  legacy_custom_smtp_billing: true, // whether to skip message overages for accounts with custom smtp https://github.com/customerio/issues/5947
  subscription_center_m1: false, // enables milestone 0 and milestone 1 features of the subscription center
  multi_language_settings: false, // enables custom multi-language settings
  exit_only_after_message: false, // enables the "exit only after receiving a message" exit option for campaigns
  parcel_amp: true, // enables AMP in Parcel
  cdp_data_catalog_enabled: false, // enabled Data Catalog menu item and page in CDP
  virtual_support_chat_enabled: false, // enables the virtual support chat via Zendesk widget
  parcel_in_hydra: false, // enables Parcel in Hydra
  in_app_legacy_editor: false, // enables the legacy in-app editor
  cdp_ui_consolidation: false, //if enabled, CDP is now presented inside the "Integrations" and no sidebar is displayed
  tourtip_adventures_v1: true,

  'goals-milestones': false, // enables the goals milestones feature
  'goals-onboarding-recommendations': false, // enables the recommendations-based goals empty state, built from the workspace's own converting conditions (FLO-4432)
  'goals-embed': false, // enables the goal-attachments-editor section (attach an existing goal) on automations, FLO-4339
  'mcp-external-account-toggle': false, // mcp external account toggle
  tasks_performance_dashboard_enabled: false, // enables the tasks performance dashboard
  segment_auto_archive: false, // enables auto-archive behavior for unused segments
  journeys_taxonomy_rename: false, // enables the Journeys taxonomy rename scaffolding
  journeys_taxonomy_rename_final: false, // enables the "final" rename state (renamed language, no transitional tooltips/parentheses)
  // Ember feature flags
  'activity-logs-time-range': false, // enables the activity logs time range feature
  'dryad-goals-builder': false, // enables the dryad-based condition builder in the goal edit drawer
  'dryad-create-segment-builder': false, // enables the dryad-based condition builder in the create segment flow
  'dryad-objects-builder': false, // enables the dryad-based condition builder for objects
  'dryad-people-search-builder': false, // enables the dryad-based condition builder in people search
  'dryad-people-export-builder': false, // enables the dryad-based condition builder in people export
  'dryad-people-bulk-delete-builder': false, // enables the dryad-based condition builder in people bulk delete
  'dryad-newsletters-builder': false, // enables the dryad-based condition builder in newsletters
  'dryad-forms-event-log-builder': false, // enables the dryad-based condition builder in the forms event log
  'dryad-action-composer-builder': false, // enables the dryad-based condition builder in the action composer
  'dryad-batch-update-builder': false, // enables the dryad-based condition builder in batch update
  'dryad-action-preconditions-builder': false, // enables the dryad-based condition builder for action preconditions
  'dryad-recipients-builder': false, // enables the dryad-based condition builder for recipients
  'dryad-in-app-broadcast-goal-builder': false, // enables the dryad-based condition builder for in-app broadcast goals
  'dryad-campaign-triggers-builder': false, // enables the dryad-based condition builder for campaign triggers
  'dryad-rel-seg-attr-trigger-builder': false, // enables the dryad-based builder for the relationship segment attribute trigger
  'dryad-attribute-trigger-builder': false, // enables the dryad-based builder for the attribute/segment campaign trigger
  'dryad-campaign-filters-builder': false, // enables the dryad-based condition builder for campaign filters
  'dryad-campaign-audience-builder': false, // enables the dryad-based builder in the campaign audience section
  'dryad-conditional-split-builder': false, // enables the dryad-based condition builder in the Conditional Split action
  'dryad-multi-split-builder': false, // enables the dryad-based condition builder for Multi Split paths
  'dryad-wait-until-builder': false, // enables the dryad-based condition builder for Wait Until paths
  'dryad-event-trigger-builder': false, // enables the dryad-based builder for the event trigger's data filters
  'dryad-conversion-goal-builder': false, // enables the dryad-based builder for the conversion goal's event data filters
  'dryad-knowledge-sources-builder': false, // enables the dryad-based builder for knowledge-sources recent-events filters
  'dryad-exit-conditions-builder': false, // enables the dryad-based builder for campaign exit conditions
  'dryad-segments-builder': false, // enables the dryad-based condition builder in segments
  'goals-create-preview': false, // enables the preview-first goal create page in place of the stepper wizard
  ai_auto_segment_names: false, // enables the AI auto segment names feature
  'deliverability-analytics': false, // enables the deliverability analytics feature
  'transactional-sms': false, // enables the transactional SMS feature
  'sms-byo-for-native': false, // lets a Customer.io-managed SMS workspace connect its own providers too
  'content-first-in-broadcast': false, // enables content and channel selection before recipients in broadcasts
  'newsletter-rate-limit-timezone': false, // enables the newsletter rate limit timezone feature
  'in-app-editor-lead-form': false, // enables the in-app editor lead form feature
  'default-from-identity': false, // enables the default from identity feature
  'email-auto-tls': false, // enables the email auto TLS feature
  configured_webhooks: false, // enables the configured webhooks feature
  'llm-action-byok': false, // enables the LLM action BYOK feature
  'in-app-forms': false, // enables the in-app forms feature
  team_member_export: false, // enables the team member export feature
  obj_wait_until: false, // enables the obj wait until feature
  'journey-attributes': false, // enables the journey attributes feature
  'whatsapp-native-free-text': false, // enables the WhatsApp native free text feature
  show_templates_snippets_layouts: false, // enables the show templates snippets layouts feature
  'in-app_global-styles': false, // enables the in-app global styles feature
  'in-app_multi-apps': false, // enables in-apps with multi-apps
  halloween_stickies_2025: false, // enables the Halloween stickies 2025 feature
  'optimized-message-library': false, // enables the optimized message library feature
  'ds-social-icons': false, // enables the social icons component feature
  'ds-countdown': false, // enables the countdown component feature
  'ds-video': false, // enables the video component feature
  'ds-ui-playground': false, // enables the design studio UI playground
  'improve-ds-agent': false, // consolidated Design Studio agent-improvements flag: the brand design system document (its Brand section on the Global Styles page), distilled org patterns, the inline AI edit quality tier, and the agent-first email creation entrypoints (DS create modal, CON-8087)

  'in-app-tooltips': false, // enables the in-app tooltips feature
  'in-app_live-preview': false, // enables the in-app live preview feature
  'ds-infinite-canvas': false, // enables the infinite canvas visual editor in Design Studio
  'ds-use-tiptap': false, // enables the tiptap-based visual editor in Design Studio
  'pause-plan-m2': false, // enables the real implementation of account pausing
  paved_road: false, // enables the CDP paved road onboarding experience
  'darker-dark-theme': false, // replaces the existing dark theme with the experimental darker layered theme
  multiple_shortlink_domains: false, // enables the multiple shortlink domains feature
  'notifications-ux': false, // enables the new notifications drawer UX
  'home-ux': false, // enables the home UX feature
  'service-accounts': false, // enables the service accounts UI on the API credentials page
  test_delivery_mode: true,
  template_engine_switch: false, // lets a workspace switch its default template engine (liquid version) even without can_update_default_template_engine
  'account-verification': false,
  push_v2: false, // Delivery-per-device push sending
  multi_app: false, // enables multiple mobile apps for push
  'ds-liquid-expressions': false, // enables liquid conditional logic in Design Studio
  gravatar: false, // enables the gravatar feature
  gravatar_enabled: false, // whether external avatars (e.g. Gravatar) are fetched for the account
  allow_agent_edit_live_data: false, // default off — admins opt in from Privacy & AI to let AI agents edit live data
  mcp_allow_edit_live_data: false, // off by default; self-serve signups get it turned on at account creation
  mcp_allow_access_sensitive_data: false, // off by default — admins opt in from AI & MCP settings
  agent_snowflake: false, // enables the direct Snowflake query endpoint for AI agents
  'agent-opus-model': false, // lets the account choose which model the agent runs on
  agent_floating_launcher: false, // replaces the "Ask agent" footer with the floating multi-conversation launcher

  cdp_salesforce_eca_migration: false,
  cdp_salesforce_eca_migration_optout: false, // when on, treats cdp_salesforce_eca_migration as off (lets accounts opt out of the ECA migration)
  cdp_track_enabled: false,
  cdp_passthrough_enabled: false, // routes Journeys Track-API data through Pipelines sources (with cdp_track_enabled, gates auto-track sources)
  databricks_source_enabled: false, // gates the Databricks source: connection form, auth-error banner and per-row reconnect state
  white_label_twilio: false, // white-labelled Twilio integration
  frequency_caps: false,
  'date-triggered-object-campaigns': false,
  'campaign-draft-mode': false, // enables draft mode for automations: editing a live campaign as a draft that publishes atomically
  'editor-breadcrumbs': false, // enables Hydra breadcrumbs on editor/composer pages
  'ds-insert-menu-previews': false, // enables insert menu previews in Design Studio
  ds_inbox_previews: false, // enables inbox previews in Design Studio
  'ds-recommended-profiles': false, // shows recommended profiles + highlights liquid syntax inline in Design Studio Preview Mode
  'phone-identifier': false, // enables using a phone number as a profile identifier
  open_tracking_consent: false, // enables the "only track opens from people who have consented" workspace setting
  profile_growth: false, // enables the Profile growth tab on the Plans & Billing page
  new_pipelines_objects_pricing: false, // enables the "new pipelines objects pricing" plan highlights on the Your Plan page
  new_pipelines_objects_billing: false, // combines person + object profile overages into one line on billing-history invoices
  builder_offer_enabled: false, // offers a Builder downgrade in the cancel flow and on the account-canceled page. Builder is paused (SPE-4075), so the offer stays hidden until Flightdeck turns this on.
  slack_agent_enabled: false, // enables the customer-installable Slack agent
  generic_saml_sso: false, // enables the Generic SAML 2.0 identity provider option on the SSO settings page
  ordway_coupon_codes: false, // enables the Ordway coupon/draft-subscription flow on the checkout page
  'legacy-twilio': false, // enables the original Twilio integration
  personal_settings_in_hydra: false, // serves /settings/personal from Hydra instead of the embedded Ember app (FPS-2861 rollout flag)
  essentials_onboarding: false, // Essentials-trial guided onboarding takeover; the cohort is trial + non-builder plan + this flag (see features/onboarding/completion.ts)
  cdp_journeys_sql_import_available: false, // shows SQL Import integrations in the directory even when no import is set up
  consent_records: false, // enables Consent Record Management feature
  'sql-sync-migration': false, // rollout flag: serves the /integrations/sql-sync tree from Hydra instead of the embedded Ember app (FPS-2307)
  reverse_etl_consolidation: false, // the SQL-sync pages forward to Pipelines Reverse ETL, which has replaced them
  'segment-cdp-integrations-migration': false, // rollout flag: serves the Segment and CDP connector routes (cdp_in, cdp_out, segment_in, segment_out) from Hydra instead of the embedded Ember app (FPS-2315)
  dashboard_editor_poc: false, // proof-of-concept Boards pages (/workspaces/:id/boards)
  'profiles-migration': false, // serves the /people (Profiles) list from Hydra instead of the embedded Ember app (FPS-2311 rollout flag)
  'workflow-builder-migration': false, // rollout flag: serves the automation workflow builder (canvas) from Hydra instead of the embedded Ember app (FPS-2338)
  'objects-migration': false, // serves the custom objects list from Hydra instead of the embedded Ember app (FPS-2707 rollout flag)
  'csv-imports-migration': false, // rollout flag: serves the /imports tree (list, upload and the setup wizard) from Hydra instead of the embedded Ember app (FPS-2306)
  'billing-ui-2026': false, // new billing UI supporting the PAYG plan
  'hydra-subscription-center': false, // rollout flag: serves /settings/subscription_center from Hydra instead of the embedded Ember app (FPS-2356)
});

const useFeatureFlags = () => {
  const account = useStore(accountStore);
  const flags = account?.feature_flags ?? {};

  return Object.assign({}, FEATURE_FLAG_DEFAULTS, flags);
};

export const useFeatureFlag = (flag: FeatureFlag | string) => {
  const flags = useFeatureFlags();
  return flags[flag] ?? false;
};

/**
 * Pure flag check for an explicit account. When the account is null (not
 * loaded yet), the flag's registered default applies — callers that render
 * before auth completes must treat that answer as provisional and re-evaluate
 * once the account arrives (subscribe to `accountStore`, or use a reactive
 * hook like `useFeatureFlag`).
 */
export const accountHasFeatureFlag = (
  account: Pick<AugmentedAccount, 'feature_flags'> | null | undefined,
  flag: FeatureFlag,
) => {
  const defaultValue = FEATURE_FLAG_DEFAULTS[flag] ?? false;

  if (account == null) {
    return defaultValue;
  }

  return account.feature_flags?.[flag] ?? defaultValue;
};

/**
 * Call-time snapshot of `accountHasFeatureFlag` against the current auth
 * store. Safe in event handlers and other post-auth code paths; do NOT use
 * during render for anything that gets cached — the pre-auth answer is the
 * flag default and won't recompute (see `useFeatureFlag`).
 */
export const hasFeatureFlag = (flag: FeatureFlag) => {
  return accountHasFeatureFlag(authStore.get()?.account, flag);
};
